import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { money, formatDate, formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import SaldoProgressBar from "@/components/SaldoProgressBar";
import {
  Users, HandCoins, Wallet, CalendarClock, Plus, ArrowRight,
} from "lucide-react";

function Kpi({ label, value, icon: Icon, hint, accent = "bg-[#D1FAE5] text-[#147D5C]" }) {
  return (
    <div className="stat-card" data-testid={`company-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#64748B] font-semibold">{label}</div>
          <div className="text-3xl font-bold tracking-tight text-[#0F172A] mt-2 tabular-nums">{value}</div>
          {hint && <div className="text-xs text-[#64748B] mt-1">{hint}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="w-[18px] h-[18px]" />
        </div>
      </div>
    </div>
  );
}

export default function CompanyDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // New advance dialog state
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", amount: 1000 });
  const [sim, setSim] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!open || !form.employee_id || !form.amount) return;
    const t = setTimeout(async () => {
      try {
        const res = await api.post("/advances/simulate", null, {
          params: { employee_id: form.employee_id, amount: Number(form.amount) },
        });
        setSim(res.data);
      } catch (e) { setSim(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [form.employee_id, form.amount, open]);

  const openCreate = () => {
    setForm({ employee_id: data?.employees?.[0]?.id || "", amount: 1000 });
    setSim(null);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/advances", { employee_id: form.employee_id, amount: Number(form.amount) });
      toast.success("Solicitud enviada");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-12"><div className="nomi-spin" /></div>;
  }

  const { company, employees, advances_month, next_liquidation, next_payday, days_to_payday, pending_count, approved_count } = data;

  return (
    <div className="space-y-6" data-testid="company-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F172A]">{company.name}</h1>
          <p className="text-sm text-[#64748B] mt-1">
            RFC <span className="font-mono text-xs">{company.rfc}</span> · Día de pago: {company.payday_day}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="company-new-advance-btn" onClick={openCreate} className="bg-[#1D9E75] hover:bg-[#147D5C]" size="lg">
              <Plus className="w-4 h-4 mr-1.5" />Solicitar adelanto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Solicitar adelanto para un empleado</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Empleado</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="cd-adv-emp"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.full_name} — {money(e.saldo?.available)} disp.
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Monto solicitado</Label>
                <Input
                  data-testid="cd-adv-amount"
                  type="number" min="1" step="1"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                />
              </div>
              {sim && (
                <div className={`rounded-md border p-4 text-sm ${sim.valid ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                  <SaldoProgressBar saldo={sim.saldo} />
                  <div className="mt-3 pt-3 border-t border-[#E2E8F0] grid grid-cols-2 gap-x-4 gap-y-1.5 tabular-nums">
                    <div className="text-[#64748B]">Monto:</div><div className="font-semibold text-right">{money(sim.amount)}</div>
                    <div className="text-[#64748B]">Descuento a nómina:</div><div className="font-semibold text-right">{money(sim.total_to_recover)}</div>
                    <div className="text-[#64748B]">Saldo disponible:</div><div className="font-semibold text-right">{money(sim.saldo?.available)}</div>
                    <div className="text-[#64748B]">Máx. 30%:</div><div className="font-semibold text-right">{money(sim.saldo?.max_allowed_30pct)}</div>
                  </div>
                  {!sim.valid && (
                    <div className="mt-3 pt-3 border-t border-rose-300 text-xs text-rose-700 space-y-0.5">
                      {sim.reasons.map((r, i) => <div key={i}>• {r}</div>)}
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={!sim?.valid} data-testid="cd-adv-submit" className="bg-[#1D9E75] hover:bg-[#147D5C]">
                  Enviar solicitud
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Empleados activos" value={data.total_employees} icon={Users} accent="bg-[#D1FAE5] text-[#147D5C]" />
        <Kpi label="Solicitudes pendientes" value={pending_count} icon={HandCoins} accent="bg-amber-50 text-amber-700" />
        <Kpi label="Adelantos del mes" value={advances_month.length} icon={HandCoins} accent="bg-blue-50 text-blue-700" />
        <Kpi
          label="Próxima nómina"
          value={days_to_payday === 0 ? "Hoy" : `${days_to_payday}d`}
          icon={CalendarClock}
          accent="bg-indigo-50 text-indigo-700"
          hint={formatDate(next_payday)}
        />
      </div>

      {/* Next liquidation summary */}
      <Card className="p-5" data-testid="next-liq-card">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-[#0F172A]">A liquidar en la próxima nómina</h2>
            <p className="text-xs text-[#64748B] mt-0.5">
              {next_liquidation.items_count} adelantos transferidos · Descuento total: <span className="font-semibold text-[#147D5C] tabular-nums">{money(next_liquidation.total_amount)}</span>
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold">Total a descontar</div>
            <div className="text-2xl font-bold text-[#147D5C] tabular-nums">{money(next_liquidation.total_amount)}</div>
          </div>
        </div>
        {next_liquidation.items.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#64748B]">
            No hay adelantos por liquidar en la próxima nómina.
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#E2E8F0] rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Empleado</th>
                  <th className="px-4 py-2 text-left font-medium">Transferido</th>
                  <th className="px-4 py-2 text-right font-medium">Descuento</th>
                </tr>
              </thead>
              <tbody>
                {next_liquidation.items.map((it) => (
                  <tr key={it.id} className="border-t border-[#E2E8F0]">
                    <td className="px-4 py-2 font-medium">{it.employee_name}</td>
                    <td className="px-4 py-2 text-xs text-[#64748B]">{formatDate(it.transferred_at)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{money(it.amount_to_discount)}</td>
                  </tr>
                ))}
                <tr className="bg-[#F8FAFC] border-t-2 border-[#1D9E75]">
                  <td className="px-4 py-2 font-bold" colSpan="2">TOTAL</td>
                  <td className="px-4 py-2 text-right font-bold tabular-nums text-[#147D5C]">{money(next_liquidation.total_amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Employees with saldo */}
      <Card className="p-0 overflow-hidden" data-testid="employees-saldo-card">
        <div className="px-5 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-[#0F172A]">Empleados · Saldo disponible hoy</h2>
            <p className="text-xs text-[#64748B] mt-0.5">Monto máximo que cada empleado podría recibir como adelanto.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => nav("/empleados")}>
            Ver todos <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
        {employees.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#64748B]">Sin empleados registrados todavía.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Empleado</th>
                  <th className="px-4 py-3 text-left font-medium">Período</th>
                  <th className="px-4 py-3 text-right font-medium">Salario mensual neto</th>
                  <th className="px-4 py-3 text-right font-medium">Saldo disp. hoy</th>
                  <th className="px-4 py-3 text-left font-medium w-[220px]">Progreso del periodo</th>
                  <th className="px-4 py-3 text-right font-medium">Máx. 30%</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) => (
                  <tr key={e.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0F172A]">{e.full_name}</div>
                      <div className="text-xs text-[#64748B] font-mono">{e.curp}</div>
                    </td>
                    <td className="px-4 py-3 text-[#475569]">
                      {e.period_days}d · <span className="text-[#64748B]">{e.saldo?.days_worked}/{e.saldo?.period_days}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(e.monthly_salary)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#147D5C]">{money(e.saldo?.available)}</td>
                    <td className="px-4 py-3">
                      <SaldoProgressBar saldo={e.saldo} compact />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#475569]">{money(e.saldo?.max_allowed_30pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Advances this month */}
      <Card className="p-0 overflow-hidden" data-testid="advances-month-card">
        <div className="px-5 py-4 border-b border-[#E2E8F0]">
          <h2 className="text-lg font-semibold tracking-tight text-[#0F172A]">Historial de adelantos del mes</h2>
          <p className="text-xs text-[#64748B] mt-0.5">{advances_month.length} solicitudes registradas este mes.</p>
        </div>
        {advances_month.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#64748B]">Sin adelantos este mes.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Fecha</th>
                  <th className="px-4 py-3 text-left font-medium">Empleado</th>
                  <th className="px-4 py-3 text-right font-medium">Monto</th>
                  <th className="px-4 py-3 text-right font-medium">Descuento</th>
                  <th className="px-4 py-3 text-left font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {advances_month.map((a) => (
                  <tr key={a.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3 text-xs text-[#64748B]">{formatDateTime(a.requested_at)}</td>
                    <td className="px-4 py-3 font-medium text-[#0F172A]">{a.employee_name}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(a.amount)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold">{money(a.total_to_recover)}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
